/**
 * Scrolls a traverser stop — a lit row or field, or the issue notice when nothing else is lit — fully
 * into view, clear of whatever sticks over the edges of the box it scrolls in.
 *
 * `scrollIntoView({ block: "start" })`, which the tables used to call, puts the row's top edge at the
 * top of its scroll box, and on the proposal pages the top of that box is under the sticky page header
 * (`ProposalStickyPageHeader`) and, inside a fee schedule, under Material React Table's sticky head as
 * well: the stop landed exactly where it could not be seen. So each scroll box the stop sits in is
 * scrolled here by hand, innermost first, with its sticky header and footer taken off its view.
 *
 * A box's sticky edges are its own children, and the head and foot of a table that is its child —
 * the page's header and footer are children of `ProposalPageMain`, and MRT's `<thead>` and `<tfoot>`
 * are one level down in its table container. Nothing deeper is looked for: a sticky element further in
 * sticks inside a box of its own, which this reaches on its own turn.
 */
export function scrollTraverserStopIntoView(target: Element): void {
  const behavior = prefersReducedMotion() ? "auto" : "smooth";
  const box = target.getBoundingClientRect();
  let top = box.top;
  let bottom = box.bottom;

  for (let scroller = scrollBoxOf(target); scroller; scroller = scrollBoxOf(scroller)) {
    const covered = stickyEdges(scroller, target);
    const scrollerBox = scroller.getBoundingClientRect();
    const viewTop = scrollerBox.top + scroller.clientTop + covered.top;
    const viewBottom = scrollerBox.top + scroller.clientTop + scroller.clientHeight - covered.bottom;

    const wanted = scroller.scrollTop + revealOffset(top, bottom, viewTop, viewBottom);
    const next = Math.min(Math.max(wanted, 0), scroller.scrollHeight - scroller.clientHeight);
    const moved = next - scroller.scrollTop;
    if (moved !== 0) scroller.scrollTo({ top: next, behavior });

    // What the next box out has to show: the stop where this box leaves it, with this box's own sticky
    // header and footer around it, so a row is not revealed with its table's head under the page's.
    top = top - moved - covered.top;
    bottom = bottom - moved + covered.bottom;
  }
}

/**
 * Scrolls one cell of a table fully into view: sideways in a table wider than its pane, then up or
 * down as a traverser stop is (#6097). A row can be in view while the cell the reader was sent to is
 * off past the right edge, which on a phone is every cell after the second.
 *
 * Sideways first and at once, not smoothly: the box that scrolls sideways is often the one that
 * scrolls up and down too, and a second smooth scroll of one box stops the first where it got to.
 */
export function scrollCellIntoView(target: Element): void {
  scrollCellIntoViewSideways(target);
  scrollTraverserStopIntoView(target);
}

/**
 * Each box the cell sits in that scrolls sideways is scrolled by hand, innermost first, with the
 * table's pinned columns taken off its view: the row's actions are pinned to the right edge, and a
 * cell revealed under them is no more visible than one left off the edge.
 */
function scrollCellIntoViewSideways(target: Element): void {
  const box = target.getBoundingClientRect();
  let left = box.left;
  let right = box.right;
  let covered = pinnedEdges(target);

  for (let scroller = sidewaysScrollBoxOf(target); scroller; scroller = sidewaysScrollBoxOf(scroller)) {
    const scrollerBox = scroller.getBoundingClientRect();
    const viewLeft = scrollerBox.left + scroller.clientLeft + covered.left;
    const viewRight = scrollerBox.left + scroller.clientLeft + scroller.clientWidth - covered.right;

    const wanted = scroller.scrollLeft + revealOffset(left, right, viewLeft, viewRight);
    const next = Math.min(Math.max(wanted, 0), scroller.scrollWidth - scroller.clientWidth);
    const moved = next - scroller.scrollLeft;
    if (moved !== 0) scroller.scrollLeft = next;

    left -= moved;
    right -= moved;
    // Pinned columns pin inside the table's own box. The boxes further out have none of their own.
    covered = { left: 0, right: 0 };
  }
}

/** The nearest ancestor that scrolls sideways and has something to scroll. */
function sidewaysScrollBoxOf(element: Element): HTMLElement | undefined {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const { overflowX } = getComputedStyle(parent);
    if (/(auto|scroll|overlay)/.test(overflowX) && parent.scrollWidth > parent.clientWidth) return parent;
  }
  return undefined;
}

/** How much of the cell's row its pinned cells cover, at the left and at the right. */
function pinnedEdges(target: Element): { left: number; right: number } {
  const edges = { left: 0, right: 0 };
  const row = target.closest("tr");
  if (!row) return edges;
  for (const cell of row.children) {
    if (cell.contains(target)) continue;
    const style = getComputedStyle(cell);
    if (style.position !== "sticky") continue;
    const width = cell.getBoundingClientRect().width;
    const stuckLeft = Number.parseFloat(style.left);
    const stuckRight = Number.parseFloat(style.right);
    if (Number.isFinite(stuckLeft)) edges.left = Math.max(edges.left, stuckLeft + width);
    else if (Number.isFinite(stuckRight)) edges.right = Math.max(edges.right, stuckRight + width);
  }
  return edges;
}

/**
 * How far to scroll so `[top, bottom]` sits inside `[viewTop, viewBottom]`: not at all when it already
 * does, the least distance when it does not, and top-aligned when it is taller than the view.
 */
function revealOffset(top: number, bottom: number, viewTop: number, viewBottom: number): number {
  if (top < viewTop || bottom - top > viewBottom - viewTop) return top - viewTop;
  if (bottom > viewBottom) return bottom - viewBottom;
  return 0;
}

/** The nearest ancestor that scrolls vertically and has something to scroll. */
function scrollBoxOf(element: Element): HTMLElement | undefined {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const { overflowY } = getComputedStyle(parent);
    if (/(auto|scroll|overlay)/.test(overflowY) && parent.scrollHeight > parent.clientHeight) return parent;
  }
  return undefined;
}

/**
 * How much of `scroller`'s view its sticky children cover at the top and at the bottom.
 *
 * A sticky element sticks inside the box's padding, not at its edge: `ProposalPageMain`'s 8px of top
 * padding put the header's `top: 0` 8px down, and leaving it out left every stop 8px under the header.
 */
function stickyEdges(scroller: HTMLElement, target: Element): { top: number; bottom: number } {
  const padding = getComputedStyle(scroller);
  const edges = { top: 0, bottom: 0 };
  for (const candidate of stickyCandidates(scroller)) {
    if (candidate.contains(target)) continue;
    const style = getComputedStyle(candidate);
    if (style.position !== "sticky") continue;
    const height = candidate.getBoundingClientRect().height;
    const stuckTop = Number.parseFloat(style.top);
    const stuckBottom = Number.parseFloat(style.bottom);
    if (Number.isFinite(stuckTop)) {
      edges.top = Math.max(edges.top, pixels(padding.paddingTop) + stuckTop + height);
    } else if (Number.isFinite(stuckBottom)) {
      edges.bottom = Math.max(edges.bottom, pixels(padding.paddingBottom) + stuckBottom + height);
    }
  }
  return edges;
}

/**
 * The scroll box's children that could stick in it, and the head and foot of a table among them.
 *
 * A child drawn with `display: contents` draws no box of its own, so its children are laid out as the
 * box's own children and stick in it the same way: they are looked through, not past. The active
 * proposal's "Prev / Next" footer is wrapped in a `TutorialTarget`, which is one, and a stop revealed
 * without it came to rest under the footer on a phone (#6097).
 */
function stickyCandidates(scroller: HTMLElement): Element[] {
  const candidates: Element[] = [];
  const visit = (parent: Element) => {
    for (const child of parent.children) {
      if (getComputedStyle(child).display === "contents") {
        visit(child);
        continue;
      }
      candidates.push(child);
      if (child.tagName === "TABLE") {
        candidates.push(...child.querySelectorAll(":scope > thead, :scope > tfoot"));
      }
    }
  };
  visit(scroller);
  return candidates;
}

function pixels(length: string): number {
  const value = Number.parseFloat(length);
  return Number.isFinite(value) ? value : 0;
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
